/* eslint-disable max-lines */
import { HIRING_RELIABILITY_CONFIG } from "@clipboard-health/contract-backend-main";
import {
  BottomSheet,
  LoadingSpinner,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { API_TYPES } from "@src/appV2/api/constants";
import { isCapacitorPlatform } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { isDeviceLocationAlwaysAllowPermissionGranted } from "@src/appV2/Location/deviceLocation/permissions";
import { isOsWithoutAlwaysAllowOption } from "@src/appV2/Location/utils";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { InterviewStatus } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { zonedTimeToUtc } from "date-fns-tz";
import { useCallback, useEffect, useRef, useState } from "react";

import { useCredentialRequirementConfiguration } from "../../Documents/useCredentialRequirementConfiguration";
import { useCreateInterview } from "../api/useCreateInterview";
import {
  PLACEMENT_TYPE,
  useCreatePlacementApplication,
} from "../api/useCreatePlacementApplication";
import {
  transformInterviewAvailabilityData,
  useGetInterviewAvailability,
} from "../api/useGetInterviewAvailability";
import { transformPlacementData, useGetPlacement } from "../api/useGetPlacement";
import { useBookingOrderingCohort } from "../hooks/useBookingOrderingCohort";
import { useJobSearchAgreement } from "../hooks/useJobSearchAgreement";
import { getInterviewAvailability, getWorkplaceStartHour } from "../utils/reliabilityRule";
import { InterviewBookedDialog } from "./InterviewBookedDialog";
import { InterviewLocationPermissionBottomSheet } from "./InterviewLocationPermissionBottomSheet";
import { InterviewReliabilityBottomSheet } from "./InterviewReliabilityBottomSheet";
import { JobSearchAgreementConfirmationModal } from "./JobSearchAgreementConfirmationModal";
import { JobSearchAgreementModal } from "./JobSearchAgreementModal";
import { PlacementDetailBookInterviewDialog } from "./PlacementDetailBookInterviewDialog";

interface CardInterviewBookingContainerProps {
  placementId: string;
  workplaceId: string;
  placementCandidateId: string;
  placementCandidate: PlacementCandidate;
  onInterviewBooked: () => Promise<void>;
  preSelectedInterviewDate?: string;
  modalState: UseModalState;
}

type BookingStep =
  | "agreement"
  | "agreementConfirmation"
  | "locationPermission"
  | "timeSelection"
  | "reliability"
  | "booked";

type DismissibleBookingStep = Exclude<BookingStep, "agreementConfirmation">;

export function CardInterviewBookingContainer(props: CardInterviewBookingContainerProps) {
  const {
    placementId,
    workplaceId,
    placementCandidateId,
    placementCandidate,
    onInterviewBooked,
    preSelectedInterviewDate,
    modalState,
  } = props;

  const worker = useDefinedWorker();
  const workerTimezone = worker.tmz ?? DEFAULT_TIMEZONE;
  const bookingReorderingCohort = useBookingOrderingCohort();
  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();
  const { showErrorToast } = useToast();

  const [currentStep, setCurrentStep] = useState<BookingStep>("timeSelection");
  const [selectedInterviewTime, setSelectedInterviewTime] = useState<
    { start: Date; end: Date } | undefined
  >(undefined);
  const [isOsWithoutAlwaysAllow, setIsOsWithoutAlwaysAllow] = useState(false);

  const timeSelectionModalState = useModalState();
  const agreementModalState = useModalState();
  const agreementConfirmationModalState = useModalState();
  const locationPermissionModalState = useModalState();
  const reliabilityModalState = useModalState();
  const interviewBookedModalState = useModalState();

  const hasInitializedRef = useRef(false);

  const {
    isJobSearchAgreementRequired,
    isLoading: isAgreementLoading,
    refetchJobSearchAgreements,
  } = useJobSearchAgreement({
    worker,
    enabled: modalState.modalIsOpen,
  });

  const { data: placementResponse } = useGetPlacement(
    { placementId, workerId: worker.userId },
    { enabled: modalState.modalIsOpen }
  );

  const placementDetails = isDefined(placementResponse)
    ? transformPlacementData(placementResponse, credentialRequirementConfiguration)
    : undefined;

  const { data: rawInterviewAvailability, refetch: refetchInterviewAvailability } =
    useGetInterviewAvailability(
      { workplaceId },
      {
        filter: {
          workerId: worker.userId,
          placementId,
          canScheduleInterviews: true,
          excludedInterviewId: placementDetails?.interviews
            .filter((interview) => interview.attributes.status === InterviewStatus.BOOKED)
            .map((interview) => interview.id)
            .join(","),
        },
      },
      { enabled: modalState.modalIsOpen && isDefined(placementDetails) }
    );

  const { mutateAsync: createInterview, isLoading: isCreatingInterview } = useCreateInterview();
  const { mutateAsync: createPlacementApplication, isLoading: isCreatingApplication } =
    useCreatePlacementApplication();

  const openTimeSelectionModal = useCallback(() => {
    setCurrentStep("timeSelection");
    timeSelectionModalState.openModal();
  }, [timeSelectionModalState]);

  const openLocationPermissionOrTimeSelection = useCallback(async () => {
    if (!isCapacitorPlatform()) {
      openTimeSelectionModal();
      return;
    }

    try {
      const hasAlwaysPermission = await isDeviceLocationAlwaysAllowPermissionGranted();
      const osWithoutAlwaysAllow = await isOsWithoutAlwaysAllowOption();
      setIsOsWithoutAlwaysAllow(osWithoutAlwaysAllow);

      if (!hasAlwaysPermission) {
        setCurrentStep("locationPermission");
        locationPermissionModalState.openModal();
        return;
      }
    } catch {
      setCurrentStep("locationPermission");
      locationPermissionModalState.openModal();
      return;
    }

    openTimeSelectionModal();
  }, [locationPermissionModalState, openTimeSelectionModal]);

  useEffect(() => {
    if (
      !modalState.modalIsOpen ||
      hasInitializedRef.current ||
      !isDefined(placementDetails) ||
      isAgreementLoading
    ) {
      return;
    }

    hasInitializedRef.current = true;

    if (isJobSearchAgreementRequired) {
      setCurrentStep("agreement");
      agreementModalState.openModal();
      return;
    }

    void openLocationPermissionOrTimeSelection();
  }, [
    modalState.modalIsOpen,
    placementDetails,
    isAgreementLoading,
    isJobSearchAgreementRequired,
    agreementModalState,
    openLocationPermissionOrTimeSelection,
  ]);

  useEffect(() => {
    if (modalState.modalIsOpen || !hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = false;
    setSelectedInterviewTime(undefined);
    setIsOsWithoutAlwaysAllow(false);
  }, [modalState.modalIsOpen]);

  const isDataReady = isDefined(placementDetails) && isDefined(rawInterviewAvailability);
  const interviewAvailability = isDataReady
    ? getInterviewAvailability({
        interviewAvailability: transformInterviewAvailabilityData(
          rawInterviewAvailability,
          workerTimezone,
          placementDetails
        ),
        workerTimezone,
        workplaceTimezone: placementDetails.workplace.tmz,
        isRescheduleForToday: false,
      })
    : [];

  const handleFlowDismiss = (step: DismissibleBookingStep) => {
    logEvent(APP_V2_APP_EVENTS.JOB_CARD_INTERVIEW_FLOW_DISMISSED, {
      placementId,
      workplaceId,
      placementCandidateId,
      source: "job_card",
      step,
    });
    modalState.closeModal();
  };

  if (!modalState.modalIsOpen) {
    return undefined;
  }

  if (!isDataReady) {
    return (
      <BottomSheet
        modalState={modalState}
        header={<BottomSheetHeader onClose={() => modalState.closeModal()} />}
        onClose={() => modalState.closeModal()}
      >
        <Stack alignItems="center" justifyContent="center" sx={{ p: 10, minHeight: "300px" }}>
          <LoadingSpinner size="large" />
        </Stack>
      </BottomSheet>
    );
  }

  const hasApplication =
    isDefined(placementDetails.applications) && placementDetails.applications.length > 0;

  const handleBookInterview = async (time: { start: Date; end: Date }) => {
    try {
      const zonedTimeStart = zonedTimeToUtc(time.start, workerTimezone);
      const zonedTimeEnd = zonedTimeToUtc(time.end, workerTimezone);

      if (hasApplication) {
        const placementApplication = placementDetails.applications?.at(-1);

        if (!isDefined(placementApplication)) {
          throw new Error("No placement application found");
        }

        await createInterview({
          relatedResource: {
            type: API_TYPES.placementApplication,
            id: placementApplication.id,
          },
          start: zonedTimeStart,
          end: zonedTimeEnd,
        });
      } else {
        await createPlacementApplication({
          placementCandidateId,
          placementDetails,
          scheduleInterview: {
            start: zonedTimeStart.toISOString(),
            end: zonedTimeEnd.toISOString(),
          },
          placementType: PLACEMENT_TYPE.verified,
          bookingReorderingCohort,
        });
      }

      logEvent(APP_V2_APP_EVENTS.INTERVIEW_CREATED, {
        placementId,
        workplaceId,
        placementCandidateId,
        source: "job_card",
      });

      reliabilityModalState.closeModal();
      setCurrentStep("booked");
      interviewBookedModalState.openModal();
      void Promise.allSettled([refetchInterviewAvailability(), onInterviewBooked()]);
    } catch {
      showErrorToast("Failed to book interview. Please try again.");
    }
  };

  const handleTimeSelected = (time: { start: Date; end: Date }) => {
    setSelectedInterviewTime(time);
    setCurrentStep("reliability");
    timeSelectionModalState.closeModal();
    reliabilityModalState.openModal();
  };

  const workplaceStartHour = selectedInterviewTime
    ? getWorkplaceStartHour({
        interviewStart: selectedInterviewTime.start,
        workplaceTimezone: placementDetails.workplace.tmz,
        workerTimezone,
        startHour: HIRING_RELIABILITY_CONFIG.WORKPLACE_START_HOUR,
      })
    : undefined;

  return (
    <>
      {currentStep === "agreement" && (
        <JobSearchAgreementModal
          modalState={agreementModalState}
          placementId={placementId}
          source="job_card"
          onClose={() => {
            handleFlowDismiss("agreement");
          }}
          onComplete={() => {
            agreementModalState.closeModal();
            void refetchJobSearchAgreements();
            setCurrentStep("agreementConfirmation");
            agreementConfirmationModalState.openModal();
          }}
        />
      )}

      {currentStep === "agreementConfirmation" && (
        <JobSearchAgreementConfirmationModal
          modalState={agreementConfirmationModalState}
          onContinueBooking={async () => {
            await openLocationPermissionOrTimeSelection();
            agreementConfirmationModalState.closeModal();
          }}
        />
      )}

      {currentStep === "locationPermission" && (
        <InterviewLocationPermissionBottomSheet
          modalState={locationPermissionModalState}
          isOsWithoutAlwaysAllow={isOsWithoutAlwaysAllow}
          onContinueClick={() => {
            logEvent(APP_V2_APP_EVENTS.INTERVIEW_LOCATION_PERMISSION_CONTINUE_CLICKED, {
              placementId,
              workplaceId,
              placementCandidateId,
              source: "job_card",
            });
          }}
          onClose={() => {
            handleFlowDismiss("locationPermission");
          }}
          onConfirm={() => {
            locationPermissionModalState.closeModal();
            openTimeSelectionModal();
          }}
        />
      )}

      {currentStep === "timeSelection" && (
        <PlacementDetailBookInterviewDialog
          placementDetails={placementDetails}
          isReschedule={false}
          modalState={timeSelectionModalState}
          interviewAvailability={interviewAvailability}
          placementId={placementId}
          preSelectedInterviewDate={preSelectedInterviewDate}
          bookingReorderingCohort={bookingReorderingCohort}
          source="job_card"
          isBookingInterview={isCreatingInterview || isCreatingApplication}
          onClose={() => {
            handleFlowDismiss("timeSelection");
          }}
          onBookInterview={handleTimeSelected}
        />
      )}

      {currentStep === "reliability" &&
        isDefined(selectedInterviewTime) &&
        isDefined(workplaceStartHour) && (
          <InterviewReliabilityBottomSheet
            modalState={reliabilityModalState}
            placementDetails={placementDetails}
            placementCandidate={placementCandidate}
            workplaceStartHour={workplaceStartHour}
            isLoading={isCreatingInterview || isCreatingApplication}
            onClose={() => {
              handleFlowDismiss("reliability");
            }}
            onContinue={() => {
              if (selectedInterviewTime) {
                void handleBookInterview(selectedInterviewTime);
              }
            }}
          />
        )}

      {currentStep === "booked" && isDefined(selectedInterviewTime) && (
        <InterviewBookedDialog
          modalState={interviewBookedModalState}
          placementDetails={placementDetails}
          selectedInterviewTime={selectedInterviewTime}
          workplaceStartHour={workplaceStartHour}
          isRecruiterScreeningEnabled={placementDetails.isRecruiterScreeningEnabled ?? false}
          onClose={() => {
            modalState.closeModal();
          }}
        />
      )}
    </>
  );
}
/* eslint-enable max-lines */
